'use client';

import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';

interface PaymentWallModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpgrade: () => void;
    onContinueFree: () => void;
    variant: 'before_pairing' | 'after_pairing';
    accountType?: 'personal' | 'business';
    couponText?: string;
    couponSubtext?: string;
}

// Value bullets with icons based on user type
const personalValueBullets = [
    { icon: '4k', text: 'Art for every mood in stunning 4K' },
    { icon: 'playlist', text: 'Create unlimited playlists' },
    { icon: 'heart', text: 'Support real artists' },
    { icon: 'app', text: 'Control from the Niio app' },
];

const businessValueBullets = [
    { icon: 'license', text: 'Licensed for commercial display' },
    { icon: 'upload', text: 'Upload your own branded content' },
    { icon: 'multi', text: 'Manage multiple screens centrally' },
    { icon: '4k', text: '4K streaming without watermarks' },
];

// Icon component for value bullets
function BulletIcon({ type }: { type: string }) {
    switch (type) {
        case '4k':
            return (
                <span className="w-5 h-5 flex items-center justify-center text-[10px] font-bold text-purple-400 bg-purple-400/20 rounded">
                    4K
                </span>
            );
        case 'playlist':
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                    />
                </svg>
            );
        case 'heart':
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                </svg>
            );
        case 'app':
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                </svg>
            );
        case 'license':
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                </svg>
            );
        case 'upload':
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                </svg>
            );
        case 'multi':
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
            );
        default:
            return (
                <svg
                    className="w-5 h-5 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            );
    }
}

export function PaymentWallModal({
    isOpen,
    onClose,
    onUpgrade,
    onContinueFree,
    variant,
    accountType = 'personal',
    couponText,
    couponSubtext,
}: PaymentWallModalProps) {
    if (!isOpen) return null;

    // Select value bullets based on account type
    const valueBullets =
        accountType === 'business'
            ? businessValueBullets
            : personalValueBullets;

    // Headlines based on variant
    const headline =
        variant === 'before_pairing'
            ? 'Great Choice!'
            : 'Unlock 4K quality and premium features';

    // Default offer text when no coupon is provided
    const offerText = couponText || 'Start Free. Stay at 50% Off.';
    const offerSubtext =
        couponSubtext || 'Your discount stays as long as you do.';

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className="max-w-md w-full relative max-h-[90vh] overflow-y-auto rounded-2xl bg-gradient-to-b from-gray-800 to-gray-900"
                >
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
                        aria-label="Close"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>

                    {/* ========== UPPER SECTION (Dark Background) ========== */}
                    <div className="p-6 sm:p-8 pb-3 text-center">
                        {/* Niio Logo - White, smaller */}
                        <div className="flex justify-center mb-4">
                            <Image
                                src="/niio_logo_white.png"
                                alt="Niio"
                                width={104}
                                height={19}
                                className="h-6 w-auto"
                                priority
                                unoptimized
                            />
                        </div>

                        {/* Headline */}
                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                            {headline}
                        </h2>

                        {/* Subheadline - reduced bottom margin */}
                        <p className="text-gray-300">
                            Unlock Your Art Experience Now
                        </p>
                    </div>

                    {/* ========== MIDDLE CARD (White Background with Shadow) ========== */}
                    <div className="mx-4 sm:mx-6 mb-6">
                        <div className="bg-white rounded-2xl p-6 text-center shadow-xl shadow-black/30">
                            {/* Limited Time Offer Badge */}
                            <div className="inline-block bg-gray-100 text-gray-600 text-sm px-4 py-1.5 rounded-full mb-4">
                                Limited time offer
                            </div>

                            {/* Main Offer Text - whitespace-nowrap to keep on one line */}
                            <div className="mb-5">
                                <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight whitespace-nowrap">
                                    {offerText}
                                </p>
                                <p className="text-gray-500 text-sm mt-2">
                                    {offerSubtext}
                                </p>
                            </div>

                            {/* Primary CTA Button - Purple */}
                            <motion.button
                                onClick={onUpgrade}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-6 rounded-full transition-colors text-lg shadow-lg shadow-purple-500/25"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                START FREE TRIAL
                            </motion.button>

                            {/* Cancel anytime text */}
                            <p className="text-gray-500 text-sm mt-3">
                                Cancel anytime—risk-free.
                            </p>
                        </div>
                    </div>

                    {/* ========== LOWER SECTION (Dark Background) ========== */}
                    <div className="px-6 sm:px-8 pb-6 sm:pb-8">
                        {/* Section Header */}
                        <p className="text-white font-semibold text-lg mb-4">
                            Everything you need for art in your space
                        </p>

                        {/* Value Bullets with Icons */}
                        <ul className="space-y-3 mb-6">
                            {valueBullets.map((bullet, index) => (
                                <li
                                    key={index}
                                    className="flex items-center text-gray-300"
                                >
                                    <span className="mr-3 flex-shrink-0">
                                        <BulletIcon type={bullet.icon} />
                                    </span>
                                    <span className="text-sm">
                                        {bullet.text}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        {/* Continue with Free Link - no underline */}
                        <div className="text-center">
                            <button
                                onClick={onContinueFree}
                                className="text-gray-400 hover:text-gray-300 text-sm transition-colors"
                            >
                                Or continue with a free account
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
